import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Typography } from '@blade/ui-kit'
import { useVisibilityFormatter } from '@root/entities/preferences'
import { AmountFormatted } from '@root/shared/core/format'

import { WalletAssetItemPrice } from '../common/wallet-asset-item/wallet-asset-item-price'

import { WalletAssetHeadline } from './wallet-asset-headline'
import { WalletAssetIcon } from './wallet-asset-icon'
import { WalletAssetCurrencyAddress } from './wallet-asset-item/wallet-asset-currency-address'

import type { WalletAssetModel } from '@root/entities/wallet/atoms'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  asset: WalletAssetModel
  onClick?: (asset: WalletAssetModel) => void
  withAddress?: boolean
  variant?: 'compact' | 'default'
} & ClassNameProps

export const WalletAssetListItem: React.FC<Props> = ({
  asset,
  onClick,
  withAddress = true,
  variant = 'default',
}) => {
  const formatHidden = useVisibilityFormatter('****')

  const [balance] = useAtom(asset.balance)
  const [balanceUsd] = useAtom(asset.balanceUsd)
  const [priceUsd] = useAtom(asset.priceUsd)

  return (
    <Root onClick={() => onClick?.(asset)}>
      <WalletAssetIcon size={variant === 'compact' ? 30 : 35} asset={asset} />
      <Info>
        <Top>
          <WalletAssetHeadline asset={asset} />
          <Balance variant="body4-semi-bold">
            {formatHidden(
              <AmountFormatted amount={balance} roundingMode="halfExpand" />,
            )}
          </Balance>
        </Top>
        <Bottom>
          <PriceContainer>
            <PriceStyled ticker={asset.currency.ticker} priceUsd={priceUsd} />
            {withAddress ? (
              <WalletAssetCurrencyAddress currency={asset.currency} />
            ) : undefined}
          </PriceContainer>

          <BalanceUsd variant="body5-semi-bold">
            {formatHidden(
              <AmountFormatted
                currency="$"
                amount={balanceUsd}
                roundingMode="halfExpand"
              />,
            )}
          </BalanceUsd>
        </Bottom>
      </Info>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 8px;
  padding-block: 12px;
  border-bottom: 1px solid var(--background-2);
`

const Info = styled.div`
  display: grid;
  gap: 1px;
`

const Top = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  justify-content: space-between;
  gap: 2px;
`

const Bottom = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  justify-content: space-between;
  gap: 2px;
`

const Balance = styled(Typography)`
  color: var(--text-12);
`

const BalanceUsd = styled(Typography)`
  color: var(--text-10);
`

const PriceContainer = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: start;
  gap: 12px;
`

const PriceStyled = styled(WalletAssetItemPrice)`
  color: var(--text-10);
  font-weight: 600;
`
